/**
 * TanStack Query-backed query client.
 *
 * @module
 */
import { QueryClient as TanstackQueryClient, QueryObserver } from '@tanstack/query-core';
const DEFAULT_STALE_MS = 5 * 60 * 1000;
const DEFAULT_GC_MS = 30 * 60 * 1000;
/**
 * The molecule contract over one TanStack `QueryClient`.
 */
class TanstackClient {
    qc;
    staleMs;
    gcMs;
    /**
     * Wraps a TanStack client.
     *
     * @param qc - The TanStack client.
     * @param config - Defaults for every query.
     */
    constructor(qc, config = {}) {
        this.qc = qc;
        this.staleMs = config.staleMs ?? DEFAULT_STALE_MS;
        this.gcMs = config.gcMs ?? DEFAULT_GC_MS;
    }
    /**
     * The molecule options as TanStack query options.
     *
     * @param options - Key, fetch, freshness.
     * @returns TanStack options with retries off.
     */
    tsOptions(options) {
        return {
            queryKey: options.key,
            queryFn: ({ signal }) => options.fetch(signal),
            staleTime: options.staleMs ?? this.staleMs,
            gcTime: options.gcMs ?? this.gcMs,
            retry: false,
        };
    }
    /**
     * The cached document, synchronously, fresh or stale.
     *
     * @param key - The document's key.
     * @returns The document, or `undefined`.
     */
    get(key) {
        return this.qc.getQueryData(key);
    }
    /**
     * The document's full state, synchronously.
     *
     * @param key - The document's key.
     * @returns The state; `idle` for an unknown key.
     */
    getState(key) {
        const q = this.qc.getQueryCache().find({ queryKey: key, exact: true });
        if (!q) {
            return {
                data: undefined,
                status: 'idle',
                error: undefined,
                isFetching: false,
                updatedAt: undefined,
            };
        }
        const s = q.state;
        const status = s.status === 'pending' ? (s.fetchStatus === 'fetching' ? 'loading' : 'idle') : s.status;
        return {
            data: s.data,
            status,
            error: s.error ?? undefined,
            isFetching: s.fetchStatus === 'fetching',
            updatedAt: s.dataUpdatedAt || undefined,
        };
    }
    /**
     * The document: cached-and-fresh at once, a fetch in flight shared, otherwise fetched.
     *
     * @param options - Key, fetch, freshness.
     * @returns The document.
     */
    fetch(options) {
        return this.qc.fetchQuery(this.tsOptions(options));
    }
    /**
     * Warms the cache; never throws; skips a fresh or in-flight document.
     *
     * @param options - Key, fetch, freshness.
     */
    prefetch(options) {
        // prefetchQuery never rejects, and skips a fresh or in-flight query.
        void this.qc.prefetchQuery(this.tsOptions(options));
    }
    /**
     * Seeds the cache.
     *
     * @param key - The document's key.
     * @param data - The document.
     */
    set(key, data) {
        const queryKey = key;
        // Cancellation is synchronous even though the returned promise is not. Never revert: the
        // state captured before this fetch would overwrite the mutation response written below.
        // An existing fetch() caller receives query-core's cancellation; later reads use `data`.
        void this.qc.cancelQueries({ queryKey, exact: true }, { revert: false });
        this.qc.setQueryData(queryKey, data);
    }
    /**
     * Marks the key and everything it prefixes as stale.
     *
     * @param key - A key or a key prefix.
     */
    invalidate(key) {
        // Not exact: every query whose key starts with these parts.
        void this.qc.invalidateQueries({ queryKey: key, refetchType: 'none' });
    }
    /**
     * Observes a document, fetching it when absent or stale.
     *
     * @param options - Key, fetch, freshness.
     * @param listener - Called with each state.
     * @returns Stops observing.
     */
    subscribe(options, listener) {
        const observer = new QueryObserver(this.qc, this.tsOptions(options));
        const relay = () => listener(this.getState(options.key));
        relay();
        // The observer refetches when the query is stale or absent; it also
        // notifies on every state change until unsubscribed.
        return observer.subscribe(relay);
    }
    /** Drops every document and every in-flight fetch. */
    clear() {
        this.qc.cancelQueries().catch(() => {
            // Cancelling is best effort; the cache is cleared regardless.
        });
        this.qc.clear();
    }
}
/**
 * Creates a TanStack Query provider.
 *
 * @param config - Provider configuration.
 * @returns A query provider.
 */
export function createProvider(config = {}) {
    return {
        name: 'tanstack',
        createClient(clientConfig) {
            const qc = config.client ??
                new TanstackQueryClient({
                    defaultOptions: {
                        queries: {
                            staleTime: clientConfig?.staleMs ?? DEFAULT_STALE_MS,
                            gcTime: clientConfig?.gcMs ?? DEFAULT_GC_MS,
                            retry: false,
                        },
                    },
                });
            return new TanstackClient(qc, clientConfig);
        },
    };
}
/** Default TanStack Query provider instance. */
export const provider = createProvider();
